import { ClearRounded, ExpandMoreRounded } from "@mui/icons-material";
import { CircularProgress } from "@mui/material";
import MuiAutocomplete, { autocompleteClasses, type AutocompleteProps } from "@mui/material/Autocomplete";
import ListSubheader from "@mui/material/ListSubheader";
import Popper from "@mui/material/Popper";
import { styled, useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import * as React from "react";
import { List, ListImperativeAPI, RowComponentProps, useListRef } from "react-window";

const StyledMuiAutocomplete = styled(MuiAutocomplete)`
  .MuiInputLabel-root {
    color: ${({ theme }) => theme.palette.grey[400]};

    &.Mui-disabled {
      color: ${({ theme }) => theme.palette.text.disabled};
    }
  }

  .MuiAutocomplete-endAdornment .MuiSvgIcon-root {
    color: ${({ theme }) => theme.palette.text.primary};
  }
` as typeof MuiAutocomplete;

const LISTBOX_PADDING = 10; // px

type ItemData = Array<
  | {
      key: number;
      group: string;
      children: React.ReactNode;
    }
  | [React.ReactElement, unknown, number]
>;

function RowComponent({
  index,
  itemData,
  getOptionLabel,
  style,
}: RowComponentProps & {
  itemData: ItemData;
  getOptionLabel: (option: unknown) => string;
}) {
  const dataSet = itemData[index];
  const inlineStyle = {
    ...style,
    top: ((style.top as number) ?? 0) + LISTBOX_PADDING,
  };

  if ("group" in dataSet) {
    return (
      <ListSubheader key={dataSet.key} component="div" style={inlineStyle}>
        {dataSet.group}
      </ListSubheader>
    );
  }

  const { key, ...optionProps } = dataSet[0];

  return (
    <Typography key={key} component="li" {...optionProps} noWrap style={inlineStyle}>
      {getOptionLabel(dataSet[1])}
    </Typography>
  );
}

// Adapter for react-window v2
const ListboxComponent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLElement> & {
    internalListRef: React.Ref<ListImperativeAPI>;
    onItemsBuilt: (optionIndexMap: Map<unknown, number>) => void;
    getOptionLabel: (option: unknown) => string;
  }
>(function ListboxComponent(props, ref) {
  const { children, internalListRef, onItemsBuilt, getOptionLabel, ...other } = props;

  const itemData: ItemData = React.useMemo(() => {
    const data: ItemData = [];
    (children as ItemData).forEach((item) => {
      data.push(item);
      if ("children" in item && Array.isArray(item.children)) {
        data.push(...item.children);
      }
    });
    return data;
  }, [children]);

  // Map option values to their indices in the flattened array
  const optionIndexMap = React.useMemo(() => {
    const map = new Map<unknown, number>();
    itemData.forEach((item, index) => {
      if (Array.isArray(item) && item[1]) {
        map.set(item[1], index);
      }
    });
    return map;
  }, [itemData]);

  React.useEffect(() => {
    if (onItemsBuilt) {
      onItemsBuilt(optionIndexMap);
    }
  }, [onItemsBuilt, optionIndexMap]);

  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up("sm"), {
    noSsr: true,
  });
  const itemCount = itemData.length;
  const itemSize = smUp ? 36 : 48;

  const getChildSize = (child: ItemData[number]) => {
    if ("group" in child) {
      return 48;
    }
    return itemSize;
  };

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
  };

  // Separate className for List, other props for wrapper div (ARIA, handlers)
  const { className, ...otherProps } = other;

  return (
    <div ref={ref} {...otherProps}>
      <List
        className={className}
        listRef={internalListRef}
        key={itemCount}
        rowCount={itemCount}
        rowHeight={(index) => getChildSize(itemData[index])}
        rowComponent={RowComponent}
        rowProps={{ itemData, getOptionLabel }}
        style={{
          height: getHeight() + 2 * LISTBOX_PADDING,
          width: "100%",
        }}
        overscanCount={5}
        tagName="ul"
      />
    </div>
  );
});

const StyledPopper = styled(Popper)({
  [`& .${autocompleteClasses.listbox}`]: {
    boxSizing: "border-box",
    "& ul": {
      padding: 0,
      margin: 0,
    },
  },
});

interface IProps<
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
> extends AutocompleteProps<T, Multiple, DisableClearable, FreeSolo> {
  isLoading?: boolean;
}

const WindowedAutocomplete = <
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
>(
  props: IProps<T, Multiple, DisableClearable, FreeSolo>,
  ref: React.Ref<unknown>,
) => {
  const { isLoading, ...other } = props;

  const getOptionLabel =
    props.getOptionLabel ?? ((option: T) => (option as { label?: string })?.label ?? String(option));

  // Use react-window v2's useListRef hook for imperative API access
  const internalListRef = useListRef(null);
  const optionIndexMapRef = React.useRef<Map<unknown, number>>(new Map());

  const handleItemsBuilt = React.useCallback((optionIndexMap: Map<unknown, number>) => {
    optionIndexMapRef.current = optionIndexMap;
  }, []);

  // Handle keyboard navigation by scrolling to highlighted option
  const handleHighlightChange = (_event: React.SyntheticEvent, option: T | null) => {
    if (option && internalListRef.current) {
      const index = optionIndexMapRef.current.get(option);
      if (index !== undefined) {
        internalListRef.current.scrollToRow({ index, align: "auto" });
      }
    }
  };

  return (
    <StyledMuiAutocomplete
      ref={ref}
      disableListWrap
      renderOption={(props, option, state) => [props, option, state.index] as React.ReactNode}
      renderGroup={(params) => params as unknown as React.ReactNode}
      onHighlightChange={handleHighlightChange}
      popupIcon={
        isLoading ? (
          <CircularProgress size={20} sx={{ color: "text.disabled" }} />
        ) : props.disabled ? null : (
          <ExpandMoreRounded sx={{ color: "primary.main", fontSize: "24px" }} />
        )
      }
      slots={{
        popper: StyledPopper,
      }}
      slotProps={
        {
          listbox: {
            component: ListboxComponent,
            internalListRef,
            onItemsBuilt: handleItemsBuilt,
            getOptionLabel,
          },
        } as unknown as AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>["slotProps"]
      }
      clearIcon={<ClearRounded sx={{ fontSize: "20px" }} />}
      {...other}
    />
  );
};

const RefForwardedWindowedAutocomplete = React.forwardRef(WindowedAutocomplete) as <
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
>(
  props: IProps<T, Multiple, DisableClearable, FreeSolo> & { ref?: React.Ref<unknown> },
) => React.ReactElement;

export { RefForwardedWindowedAutocomplete as WindowedAutocomplete };
